/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
} from "@tanstack/react-query";

import { isValid } from "date-fns";

import { Box } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { CalendarToday, DateRange } from "@mui/icons-material";

import StatCard from "views/Dashboard/components/StatCard";
import BeltrameActivityItemTable from "views/Profiles/components/BeltrameActivityItemTable/BeltrameActivityItemTable";
import ActivityItemProfile from "views/Profiles/components/ActivityItemProfile/ActivityItemProfile";

import GridContainer from "components/UI/Grid/GridContainer";
import GridItemTS from "components/UI/Grid/GridItemTS";

import {
  ActivityItemStatusEnum,
  ActivityItemValidationStatusEnum,
  VisibleActivityEnum,
} from "utils/@types";
import {
  compareStringsIgnoreCaseAndRemoveSpecialChars,
  renderToastUnique,
} from "utils/functions";

import { useClientConfig } from "hooks/useClientConfig";

import { saveDateOnUrlParams } from "helpers/DateHelper";
import { useActivityItemContext } from "store/activityItem";
import { useSowContext } from "store/sow";
import { LocalizationProvider } from "@mui/x-date-pickers";

interface ActivityItemsTabProps<T> {
  isActivityItemsInitialLoading?: boolean;
  refetchActivityItems: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>,
  ) => Promise<QueryObserverResult<T[], unknown>>;
  workItems?: any;
  isWorkItemsLoading?: boolean;
  refetchWorkItems?: any;
  profile: "WORKER" | "SOW";
  date: Date;
  setDate: React.Dispatch<React.SetStateAction<Date>>;
  fieldglassId: string;
  entityDate?: {
    start: string;
    end: string;
  };
  supplierCode?: string;
}

const ActivityItemsTab: React.FC<ActivityItemsTabProps<any>> = ({
  isActivityItemsInitialLoading,
  refetchActivityItems,
  // workItems = [], //Revisar os arquivos para remover o workItems
  // isWorkItemsLoading = false, //Revisar os arquivos para remover o workItems
  // refetchWorkItems, //Revisar os arquivos para remover o workItems
  profile,
  date,
  setDate,
  fieldglassId,
  entityDate,
  supplierCode,
}) => {
  const { t } = useTranslation("activity-item-table");

  const {
    activityItems,
    isLoading: isActivityItemsLoading,
    isRefetching: isActivityItemsRefetching,
    setFilteredActivityItems,
  } = useActivityItemContext((state) => ({
    activityItems: state.activityItems,
    filteredActivityItems: state.filteredActivityItems,
    isLoading: state.isLoading,
    isRefetching: state.isRefetching,
    setFilteredActivityItems: state.setFilteredActivityItems,
  }));

  useEffect(() => {
    if (activityItems?.length >= 0) setFilteredActivityItems(activityItems);
  }, [activityItems, setFilteredActivityItems]);

  const createToast = useCallback<typeof renderToastUnique>(
    ({ toastId, message, type, options }) => {
      return renderToastUnique({ toastId, message, type, options });
    },
    [],
  );

  useEffect(() => {
    if (!isActivityItemsInitialLoading) {
      isActivityItemsRefetching
        ? createToast({
            toastId: "unique-toast-fetch",
            message: t("refetching-activity-items"),
            type: "loading",
          })
        : createToast({
            toastId: "unique-toast-fetch",
            message: t("success-refetching-activity-items"),
            type: "success",
          });
    }
  }, [isActivityItemsRefetching]);

  const activityItemsSummary = activityItems?.reduce(
    (
      summary: {
        activityItemsPending: number;
        activityItemsApproved: number;
        activityItemsRejected: number;
      },
      ai: { activityItemStatus: string; validationStatus: string },
    ) => {
      const activityStatus = compareStringsIgnoreCaseAndRemoveSpecialChars(
        ai.activityItemStatus,
        ActivityItemStatusEnum.PENDING,
      );
      const validationStatusApproved = compareStringsIgnoreCaseAndRemoveSpecialChars(
        ai.validationStatus,
        ActivityItemValidationStatusEnum.APPROVED,
      );
      const validationStatusRejected = compareStringsIgnoreCaseAndRemoveSpecialChars(
        ai.validationStatus,
        ActivityItemValidationStatusEnum.REJECTED,
      );

      if (activityStatus) {
        summary.activityItemsPending++;
      }

      if (validationStatusApproved) {
        summary.activityItemsApproved++;
      }

      if (validationStatusRejected) {
        summary.activityItemsRejected++;
      }

      return summary;
    },
    {
      activityItemsPending: 0,
      activityItemsApproved: 0,
      activityItemsRejected: 0,
    },
  ) || {
    activityItemsPending: 0,
    activityItemsApproved: 0,
    activityItemsRejected: 0,
  };

  const activityItemSelected = useSowContext((ctx) => ctx.activityItemSelected);

  const {
    isActivityItemsGeneralVisible,
    viewActivities,
    handleChangeViewActivities,
  } = useClientConfig();

  return (
    <GridContainer data-cy="activity-items-tab">
      <GridItemTS xs={12} key="date-filter" style={{ marginBottom: 20 }}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Box minWidth="20rem">
            {viewActivities == VisibleActivityEnum.MONTHLY && (
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={ptBR}
              >
                <DatePicker
                  views={["year", "month"]}
                  format="MM/yyyy"
                  className="datePicker"
                  label={t("Select Due Date")}
                  sx={{ fontSize: "1rem", textAlign: "center" }}
                  onChange={(d) => {
                    if (typeof d !== "string") {
                      if (isValid(d)) {
                        setDate(d);
                        saveDateOnUrlParams(d);
                      }
                    }
                  }}
                  value={date}
                  closeOnSelect
                  disabled={viewActivities !== VisibleActivityEnum.MONTHLY}
                  data-cy="date-picker"
                />
              </LocalizationProvider>
            )}
          </Box>

          {isActivityItemsGeneralVisible && (
            <Box>
              <ToggleButtonGroup
                value={viewActivities}
                exclusive
                onChange={handleChangeViewActivities}
                color="primary"
              >
                <ToggleButton
                  disabled={viewActivities === VisibleActivityEnum.MONTHLY}
                  value={VisibleActivityEnum.MONTHLY}
                  aria-label={VisibleActivityEnum.MONTHLY}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    gridGap={6}
                  >
                    <DateRange /> <span>{t("monthly")}</span>
                  </Box>
                </ToggleButton>
                <ToggleButton
                  disabled={viewActivities === VisibleActivityEnum.FULL}
                  value={VisibleActivityEnum.FULL}
                  aria-label={VisibleActivityEnum.FULL}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    gridGap={6}
                  >
                    <CalendarToday /> <span>{t("general")}</span>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>
      </GridItemTS>

      <GridItemTS key="cards-status-grid" xs={12}>
        <GridContainer key="stat-cards">
          <StatCard
            color="info"
            value={activityItems?.length ?? 0}
            isLoading={isActivityItemsLoading}
            statText={t("activityItems")}
            icon="document_scanner"
            xs={12}
            md={6}
            lg={3}
            content=""
            onClick={() => setFilteredActivityItems(activityItems)}
            data-cy="activity-items-stat-card"
          />

          <StatCard
            color="warning"
            value={activityItemsSummary.activityItemsPending}
            isLoading={isActivityItemsLoading}
            statText={t("pending")}
            icon="hourglass_empty"
            xs={12}
            md={6}
            lg={3}
            content=""
            onClick={() =>
              setFilteredActivityItems(
                activityItems?.filter((ai: { activityItemStatus: string }) =>
                  compareStringsIgnoreCaseAndRemoveSpecialChars(
                    ai.activityItemStatus,
                    ActivityItemStatusEnum.PENDING,
                  ),
                ),
              )
            }
            data-cy="activity-items-pending-stat-card"
          />

          <StatCard
            color="success"
            value={activityItemsSummary.activityItemsApproved}
            isLoading={isActivityItemsLoading}
            statText={t("approved")}
            icon="check_circle"
            xs={12}
            md={6}
            lg={3}
            content=""
            onClick={() =>
              setFilteredActivityItems(
                activityItems?.filter((ai: { validationStatus: string }) =>
                  compareStringsIgnoreCaseAndRemoveSpecialChars(
                    ai.validationStatus,
                    ActivityItemValidationStatusEnum.APPROVED,
                  ),
                ),
              )
            }
            data-cy="activity-items-approved-stat-card"
          />

          <StatCard
            color="danger"
            value={activityItemsSummary.activityItemsRejected}
            isLoading={isActivityItemsLoading}
            statText={t("rejected")}
            icon="cancel"
            xs={12}
            md={6}
            lg={3}
            content=""
            onClick={() =>
              setFilteredActivityItems(
                activityItems?.filter((ai: { validationStatus: string }) =>
                  compareStringsIgnoreCaseAndRemoveSpecialChars(
                    ai.validationStatus,
                    ActivityItemValidationStatusEnum.REJECTED,
                  ),
                ),
              )
            }
            data-cy="activity-items-rejected-stat-card"
          />
        </GridContainer>
      </GridItemTS>

      {activityItemSelected ? (
        <ActivityItemProfile
          refID={fieldglassId}
          profile={profile}
          entityDate={entityDate}
          supplierCode={supplierCode}
        />
      ) : (
        <BeltrameActivityItemTable
          refetchActivityItems={() => refetchActivityItems()}
          refID={fieldglassId}
          profile={profile}
          entityDate={entityDate}
          supplierCode={supplierCode}
          data-cy="beltrame-activity-item-table"
        />
      )}
    </GridContainer>
  );
};

export default ActivityItemsTab;
